import { ActionIcon, Box, Card, Group, Modal, Text, Tooltip, Transition } from '@mantine/core';
import { GlobalWorkerOptions } from 'pdfjs-dist';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFilePreviewStore } from '@mygpt/app/chat/stores/useFilePreviewStore';
import { Content } from './Content';
import { IconX } from '@tabler/icons-react';
import { TableRAGContent } from '@mygpt/app/chat/components/PDFViewer/TableRAGContent';

interface PDFViewerProps {
  // 是否启用嵌入式UI，默认是弹窗式UI
  embedded?: boolean;
  // 弹窗宽度，默认维持现有页面的 70%
  modalSize?: string | number;
  // 是否让 PDF 页面根据弹窗内容区宽度缩放
  fitPdfToWidth?: boolean;
  // 关闭行为，默认清空引用信息收起面板。
  // 独立预览页需要覆盖为关闭标签页，否则清空后只剩一张白页。
  onClose?: () => void;
  // 独立整页展示：去掉侧栏形态的左侧留白与左边框。
  // embedded 原本是聊天区右侧的分栏面板，那些间距在整页下是多余的。
  standalone?: boolean;
}

GlobalWorkerOptions.workerSrc = `/pdf.worker.min.js?t=${Date.now()}`;

export const PDFViewer: React.FC<PDFViewerProps> = ({
  embedded = false,
  modalSize = '70%',
  fitPdfToWidth = false,
  onClose,
  standalone = false,
}) => {
  const { t } = useTranslation();
  const { file_id, rows, title, clearReferenceInfo, sheet_name } = useFilePreviewStore();
  const handleClose = onClose ?? clearReferenceInfo;

  const getTitle = () => {
    if (!title) return t('preview_panel.title');

    // 如果是 TableRAG 的引用，也将 sheet 名称展示出来
    if (rows.length) {
      return `${title} - ${sheet_name}`;
    }
    return title;
  };

  if (embedded) {
    return (
      <div
        className="transition-all bg-[#fafbff] h-full"
        style={{
          position: 'relative',
          flex: '1',
          overflow: 'hidden',
        }}
      >
        <Transition
          mounted={true}
          transition="fade-left"
          duration={150}
          timingFunction="ease-out"
          enterDelay={100}
          keepMounted
        >
          {(styles) => (
            <div className={`h-full flex-1 ${standalone ? '' : 'ml-2'}`} style={styles}>
              <Card
                className={standalone ? '' : 'border-l'}
                h="100%"
                style={{
                  borderRadius: 0,
                }}
              >
                <Card.Section inheritPadding py="xs">
                  <Group justify="space-between">
                    <Text fw={500}>{getTitle()}</Text>
                    <Tooltip label={t('preview_panel.close')}>
                      <ActionIcon variant="subtle" color="gray" onClick={handleClose}>
                        <IconX size={18} />
                      </ActionIcon>
                    </Tooltip>
                  </Group>
                </Card.Section>
                <Card.Section h={'100%'}>
                  <Box className="flex flex-col relative bg-black/40 h-full">
                    {rows.length ? <TableRAGContent /> : <Content />}
                  </Box>
                </Card.Section>
              </Card>
            </div>
          )}
        </Transition>
      </div>
    );
  }

  return (
    <Modal
      opened={!!file_id}
      onClose={handleClose}
      centered
      transitionProps={{
        transition: 'fade',
        duration: 600,
        timingFunction: 'ease',
      }}
      size={modalSize}
      padding={0}
      classNames={{
        header: 'p-4 m-0 bg-black/70 text-white',
        close: 'text-white hover:bg-black/50',
      }}
      title={getTitle()}
      zIndex={500}
    >
      <Box className="flex flex-col relative bg-black/40 h-[80dvh]">
        {rows.length ? <TableRAGContent /> : <Content fitWidth={fitPdfToWidth} />}
      </Box>
    </Modal>
  );
};
